import { loadLanguage } from "@/utils/storage";
import { defineStore } from "pinia";
import { useUserStore } from "@/store/user";
import { useVisitStore } from "@/store/visit";
import { FeatureParticipation } from "@/interfaces/Feature/FeatureParticipation.ts";
import {
  FEATURE_AGE_VERIFICATION,
  FEATURE_CC_DISCOUNT,
  FEATURE_CS_DISCOUNT,
  FEATURE_DOUBLE_OPT_IN,
  FEATURE_FEEDBACK,
  FEATURE_FREEAREA_VIDEO_PREVIEW,
  FEATURE_FREECHAT,
  FEATURE_FRENCH,
  FEATURE_LADIES_DISCOUNT,
  FEATURE_MRSKIN_DISCOUNT,
  FEATURE_ORGANIC_POPUP,
  FEATURE_TRAILER,
  FEATURE_VAT,
  FEATURE_VD_DISCOUNT_26,
  FEATURE_VOUCHER_TRIAL,
} from "@/utils/variables.ts";

interface FeatureState {
  feedbackPopup: {
    show: boolean;
    type: string | null;
  };
  introducePopup: {
    show: boolean;
    type: string | null;
  };
}
export const useFeatureStore = defineStore("feature", {
  state: (): FeatureState => ({
    feedbackPopup: {
      show: false,
      type: null,
    },
    introducePopup: {
      show: false,
      type: null,
    },
  }),

  getters: {
    showFeedbackPopup: (state: FeatureState) => state.feedbackPopup.show,
    feedbackPopupType: (state: FeatureState) => state.feedbackPopup.type,
    showIntroducePopup: (state: FeatureState) => state.introducePopup.show,
    introducePopupType: (state: FeatureState) => state.introducePopup.type,

    getFeature:
      () =>
      (featureName: string, byVisit: boolean = false): FeatureParticipation | null => {
        let feature;

        if (byVisit) {
          const visitStore = useVisitStore();
          const visit = visitStore.getVisit;
          feature = visit?.features?.[featureName];
        } else {
          const userStore = useUserStore();
          const profile = userStore.getProfile;
          feature = profile?.features?.[featureName];
        }

        return feature || null;
      },

    isFeatureActive:
      () =>
      (featureName: string, byVisit: boolean = false) => {
        const feature = useFeatureStore().getFeature(featureName, byVisit);
        return feature?.is_active || false;
      },

    hasFrench(): boolean {
      const isVisitFeatureActive = this.isFeatureActive(FEATURE_FRENCH, true);
      const isProfileFeatureActive = this.isFeatureActive(FEATURE_FRENCH);

      return isVisitFeatureActive || isProfileFeatureActive || loadLanguage() === "fr";
    },
    hasVAT(): boolean {
      return this.isFeatureActive(FEATURE_VAT);
    },
    hasCCDiscount(): boolean {
      return this.isFeatureActive(FEATURE_CC_DISCOUNT);
    },
    hasFeedbackFeature(): boolean {
      return this.isFeatureActive(FEATURE_FEEDBACK);
    },
    hasMrSkinDiscountFeature(): boolean {
      return this.isFeatureActive(FEATURE_MRSKIN_DISCOUNT);
    },
    hasDiscountFeature(): boolean {
      return this.isFeatureActive(FEATURE_VD_DISCOUNT_26);
    },
    hasLadiesDiscountFeature(): boolean {
      return this.isFeatureActive(FEATURE_LADIES_DISCOUNT);
    },
    hasCustomerSupportDiscountFeature(): boolean {
      return this.isFeatureActive(FEATURE_CS_DISCOUNT);
    },
    hasTrailerFeature(): boolean {
      return this.isFeatureActive(FEATURE_TRAILER);
    },
    hasFreechatFeature(): boolean {
      return this.isFeatureActive(FEATURE_FREECHAT);
    },
    hasDoubleOptInFeature(): boolean {
      return this.isFeatureActive(FEATURE_DOUBLE_OPT_IN);
    },
    hasOrganicPopupFeature(): boolean {
      return this.isFeatureActive(FEATURE_ORGANIC_POPUP);
    },
    hasFreeareaVideoPreviewFeature(): boolean {
      return this.isFeatureActive(FEATURE_FREEAREA_VIDEO_PREVIEW, true);
    },
    hasAnyDiscountFeature(): boolean {
      return this.hasMrSkinDiscountFeature || this.hasLadiesDiscountFeature || this.hasCustomerSupportDiscountFeature || this.hasDiscountFeature;
    },
    getLandingUrl(): string {
      const userStore = useUserStore();

      if (!userStore.isLoggedIn) {
        return "/";
      }

      return "/recent_updates";
      // todo: make another experiment
      //return this.isFeatureActive(FEATURE_LANDING_FEEDS_REDIRECT) ? "/feeds" : "/recent_updates";
    },
    hasAgeVerificationFeature(): boolean {
      return this.isFeatureActive(FEATURE_AGE_VERIFICATION, !useUserStore().isLoggedIn);
    },
    hasVoucherTrialFeature(): boolean {
      return this.isFeatureActive(FEATURE_VOUCHER_TRIAL);
    },
  },

  actions: {
    setFeedbackPopup(show: boolean, type: string) {
      this.feedbackPopup.show = show;
      this.feedbackPopup.type = type;
    },
    setIntroducePopup(show: boolean, type: string) {
      this.introducePopup.show = show;
      this.introducePopup.type = type;
    },
  },
});
